import { useState, useEffect } from "react";
import {
  LeaveStatus,
  getLeaveStatusLabel,
  getLeaveStatusClass,
  getLeaveDayPartLabel,
} from "../../constants/constants";
import { employeeDetailCache, fetchEmployeeFull } from "../../utils/employeeCache";

const formatDesignationOf = (obj) => {
  if (!obj) return null;
  if (typeof obj.designation === "object" && obj.designation) return obj.designation.name || null;
  if (typeof obj.designation === "string" && obj.designation) return obj.designation;
  return obj.designation_name || null;
};

const formatDepartmentOf = (obj) => {
  if (!obj) return null;
  if (typeof obj.department === "object" && obj.department) return obj.department.name || null;
  if (typeof obj.department === "string" && obj.department) return obj.department;
  return obj.department_name || null;
};

// Shown only for department/designation while their one-time fetch is in
// flight and nothing is known yet — "—" would read as "no data", this
// reads as "still loading", which is the true state. Emp ID/Name never
// need this since they always have fallback data available immediately.
// In practice this rarely shows now: Leaves.jsx prefetches every visible
// employee's full record as soon as the leave list loads, so by the time
// this popup opens the cache (imported from utils/employeeCache) usually
// already has the record and this renders the real value on first paint.
const FieldSkeleton = () => (
  <span
    style={{
      display: "inline-block",
      width: 90,
      height: 14,
      borderRadius: 4,
      background: "var(--border-color, #e2e8f0)",
      opacity: 0.6,
      animation: "viewleave-pulse 1.1s ease-in-out infinite",
    }}
  />
);

/**
 * Leave-detail popup.
 *
 * Props:
 *  - leave:            the leave request being viewed
 *  - employees:         flat employee list, used as a fallback match when the API's
 *                       embedded employee_detail is thin
 *  - canApprove:        whether Approve/Reject should show (vs. Cancel for the requester)
 *  - onClose:           close the popup
 *  - onApprovalAction:  (leaveId, action) => void — parent owns the confirm modal + API call
 */
const ViewLeave = ({ leave, employees, canApprove, onClose, onApprovalAction }) => {
  const [viewTab, setViewTab] = useState("personal");

  const empId = leave.employee ?? leave.employee_detail?.id;

  // Cache is shared with Leaves.jsx, which prefetches every visible
  // employee's full record as soon as the leave list loads. By the time
  // this component mounts, empId is very likely already in the cache —
  // this read is what makes department/designation appear immediately
  // instead of waiting on a fresh fetch.
  const [viewEmpFull, setViewEmpFull] = useState(
    () => (empId ? employeeDetailCache.get(empId) : null) || null
  );
  // Only "loading" if we don't already have a cached full record.
  const [empLoading, setEmpLoading] = useState(() => !!empId && !employeeDetailCache.has(empId));

  // Falls back to fetching directly only if the prefetch in Leaves.jsx
  // hasn't completed yet (e.g. clicked View very quickly, or the employee
  // wasn't on the currently-loaded page). fetchEmployeeFull dedupes against
  // any prefetch already in flight, so this never double-fetches.
  useEffect(() => {
    if (!empId) {
      setViewEmpFull(null);
      setEmpLoading(false);
      return;
    }

    const cached = employeeDetailCache.get(empId);
    if (cached) {
      setViewEmpFull(cached);
      setEmpLoading(false);
      return;
    }

    let cancelled = false;
    setEmpLoading(true);
    fetchEmployeeFull(empId).then((full) => {
      if (!cancelled) {
        setViewEmpFull(full);
        setEmpLoading(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [empId]);

  const empDetail = leave.employee_detail || {};

  const matchedEmployee = employees.find((employee) => {
    const targetId = leave.employee ?? empDetail.id;
    if (employee.id === targetId) return true;
    if (empDetail.emp_id && employee.emp_id === empDetail.emp_id) return true;

    const targetName = leave.employee_name || empDetail.full_name;
    if (targetName) {
      return (
        (employee.full_name || employee.name || "").trim().toLowerCase() ===
        targetName.trim().toLowerCase()
      );
    }
    return false;
  });

  // Emp ID / Name have real fallback data (employee_detail / matched
  // dropdown entry / the leave record itself), so these render correctly
  // on the very first paint — no skeleton needed.
  const viewEmpId =
    viewEmpFull?.emp_id || empDetail.emp_id || matchedEmployee?.emp_id || leave.emp_id || "—";

  const viewName =
    viewEmpFull?.full_name ||
    empDetail.full_name ||
    matchedEmployee?.full_name ||
    matchedEmployee?.name ||
    leave.employee_name ||
    "—";

  // Department / Designation only ever come from the full employee fetch
  // (or the cache) — the dropdown list doesn't carry them. While that's in
  // flight and nothing is known yet, show a skeleton instead of "—". Thanks
  // to the Leaves.jsx prefetch this is almost always already resolved by
  // the time this renders.
  const deptKnown =
    formatDepartmentOf(viewEmpFull) ||
    formatDepartmentOf(empDetail) ||
    formatDepartmentOf(matchedEmployee) ||
    formatDepartmentOf(leave);
  const desigKnown =
    formatDesignationOf(viewEmpFull) ||
    formatDesignationOf(empDetail) ||
    formatDesignationOf(matchedEmployee) ||
    formatDesignationOf(leave);

  const viewDept = deptKnown || (empLoading ? null : "—");
  const viewDesig = desigKnown || (empLoading ? null : "—");

  return (
    <div className="popup-overlay">
      <style>{`
        @keyframes viewleave-pulse {
          0%, 100% { opacity: 0.35; }
          50% { opacity: 0.75; }
        }
      `}</style>
      <div
        className="popup-content premium-profile sticky-shell"
        style={{ maxHeight: "85vh" }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* STICKY HEADER */}
        <div className="page-header" style={{ marginBottom: 6 }}>
          <h2 style={{ fontSize: 18 }}>Leave Request Details</h2>
        </div>

        <hr className="premium-divider" />

        <div className="profile-tabs">
          <button
            className={viewTab === "personal" ? "active" : ""}
            onClick={() => setViewTab("personal")}
          >
            Employee Information
          </button>
          <button
            className={viewTab === "leave" ? "active" : ""}
            onClick={() => setViewTab("leave")}
          >
            Leave Details
          </button>
        </div>

        {/* SCROLLABLE BODY */}
        <div className="popup-scroll-body">
          {/* EMPLOYEE INFORMATION */}
          {viewTab === "personal" && (
            <div className="profile-grid">
              <div className="profile-card">
                <label>Emp ID</label>
                <span>{viewEmpId}</span>
              </div>
              <div className="profile-card">
                <label>Name</label>
                <span>{viewName}</span>
              </div>
              <div className="profile-card">
                <label>Department</label>
                {viewDept === null ? <FieldSkeleton /> : <span>{viewDept}</span>}
              </div>
              <div className="profile-card">
                <label>Designation</label>
                {viewDesig === null ? <FieldSkeleton /> : <span>{viewDesig}</span>}
              </div>
            </div>
          )}

          {/* LEAVE DETAILS */}
          {viewTab === "leave" && (
            <>
              <div className="profile-grid">
                <div className="profile-card">
                  <label>Leave Type</label>
                  <span>{leave.leave_type_detail?.name || leave.leave_type_name || "—"}</span>
                </div>

                <div className="profile-card">
                  <label>Status</label>
                  <span className={`leave-status ${getLeaveStatusClass(leave.status)}`}>
                    {getLeaveStatusLabel(leave.status)}
                  </span>
                </div>

                <div className="profile-card">
                  <label>From</label>
                  <span>{leave.start_date}</span>
                </div>

                <div className="profile-card">
                  <label>To</label>
                  <span>{leave.end_date}</span>
                </div>

                <div className="profile-card">
                  <label>Days</label>
                  <span>{leave.total_days ?? "—"}</span>
                </div>

                <div className="profile-card">
                  <label>Day Part</label>
                  <span>{getLeaveDayPartLabel(leave.day_part)}</span>
                </div>
              </div>

              <div className="profile-card" style={{ marginTop: 16, gridColumn: "1 / -1" }}>
                <label>Reason</label>
                <span style={{ fontWeight: 400 }}>{leave.reason || "—"}</span>
              </div>

              {leave.remarks && (
                <div className="profile-card" style={{ marginTop: 12, gridColumn: "1 / -1" }}>
                  <label>Remarks</label>
                  <span style={{ fontWeight: 400 }}>{leave.remarks}</span>
                </div>
              )}
            </>
          )}
        </div>

        {/* STICKY FOOTER */}
        <div
          className="employee-page-footer"
          style={{ padding: "22px 0 0", margin: "24px 0 0", justifyContent: "flex-end" }}
        >
          <div className="employee-actions">
            {canApprove && leave.status === LeaveStatus.PENDING && (
              <>
                <button
                  className="primary"
                  style={{ background: "#28a745" }}
                  onClick={() => onApprovalAction(leave.id, "approve")}
                >
                  Approve
                </button>
                <button
                  className="primary"
                  style={{ background: "#dc3545" }}
                  onClick={() => onApprovalAction(leave.id, "reject")}
                >
                  Reject
                </button>
              </>
            )}

            {!canApprove && leave.can_cancel && (
              <button
                className="primary"
                style={{ background: "#f59e0b" }}
                onClick={() => onApprovalAction(leave.id, "cancel")}
              >
                Cancel Leave
              </button>
            )}

            <button className="secondary" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewLeave;